import React, { ReactElement, ComponentProps, useMemo } from 'react';

import PropTypes from 'prop-types';

import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import AvatarGroupContext from './AvatarGroupContext';
import {
  AvatarSize,
  AVATAR_SIZES,
  AVATAR_GROUP_VARIANTS,
  AVATAR_GROUP_TRUNCATION,
  AVATAR_GROUP_VARIANTS_MAX_SIZE,
  AVATAR_GROUP_TRUNCATION_MAX_SIZE,
  AvatarGroupVariant,
  AvatarGroupTruncation,
} from './constants';
import Truncation from './Truncation';

type AvatarGroupProps = StylableComponent<HTMLSpanElement> & {
  max?: number;
  size?: AvatarSize;
  variant?: AvatarGroupVariant;
  truncation?: AvatarGroupTruncation;
  renderTruncation?: (props: ComponentProps<typeof Truncation>) => ReactElement;
};

/**
 * User avatars can be grouped together in a bundle pattern to show multiple
 * contributors. A bundle is limited to 3 or 6 avatars, depending on the
 * layout size.
 */
const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(
  (
    {
      className,
      style,
      children: childrenProp,
      max,
      size = AVATAR_SIZES.MEDIUM_32,
      variant = AVATAR_GROUP_VARIANTS.COMPACT,
      truncation = AVATAR_GROUP_TRUNCATION.NUMERIC,
      renderTruncation,
      ...props
    },
    ref,
  ) => {
    const context = useMemo(() => ({ size }), [size]);
    const AvatarTruncation = renderTruncation || Truncation;
    const children = React.Children.toArray(childrenProp).filter((child) =>
      React.isValidElement(child),
    );
    const clampedMax = max || AVATAR_GROUP_VARIANTS_MAX_SIZE[variant];
    const hasTruncation = children.length > clampedMax;
    const displayedAvatars = children.length - (children.length - clampedMax);
    const isOverMaxTruncationCount =
      children.length - clampedMax >= AVATAR_GROUP_TRUNCATION_MAX_SIZE[size];
    const count = Math.min(
      children.length - clampedMax + 1,
      AVATAR_GROUP_TRUNCATION_MAX_SIZE[size],
    );

    return (
      <div
        ref={ref}
        className={className}
        css={[
          {
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
          style,
        ]}
        {...props}
      >
        <AvatarGroupContext.Provider value={context}>
          {children
            .slice(0, hasTruncation ? displayedAvatars - 1 : displayedAvatars)
            .concat(
              hasTruncation
                ? [
                    <AvatarTruncation
                      key="extra"
                      count={count}
                      truncation={(() => {
                        // If the count is greater than the max allowed for current size
                        // we should switch to "..." to prevent showing an incorrect count.
                        if (truncation === AVATAR_GROUP_TRUNCATION.NUMERIC) {
                          return isOverMaxTruncationCount
                            ? AVATAR_GROUP_TRUNCATION.DOTTED
                            : AVATAR_GROUP_TRUNCATION.NUMERIC;
                        }
                        return truncation;
                      })()}
                      size={size}
                    />,
                  ]
                : [],
            )}
        </AvatarGroupContext.Provider>
      </div>
    );
  },
);

AvatarGroup.displayName = 'AvatarGroup';

AvatarGroup.propTypes = {
  /**  The component's children */
  children: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Maximum amount of avatars to show before grouping */
  max: PropTypes.number,
  /** Set the size of the avatar */
  size: PropTypes.oneOf(Object.values(AVATAR_SIZES)),
  /** Set the size of the avatar */
  variant: PropTypes.oneOf(Object.values(AVATAR_GROUP_VARIANTS)),
  /** Set the size of the avatar */
  truncation: PropTypes.oneOf(Object.values(AVATAR_GROUP_TRUNCATION)),
  /** render prop for Truncation component, useful for rendering Flyout above Truncation */
  renderTruncation: PropTypes.func,
};

export default Object.assign(AvatarGroup, {
  SIZES: AVATAR_SIZES,
  VARIANTS: AVATAR_GROUP_VARIANTS,
  TRUNCATION: AVATAR_GROUP_TRUNCATION,
  Truncation,
});
